import { Style } from "@glamcorner/gc-types/generated/types";
import { ErrorObject } from "ajv";
import { AxiosError } from "axios";

export type SearchAppQuerySortField = "entity_id" | "position" | "price";
export type SearchAppQuerySortDirection = "desc" | "asc";

export enum SortingBy {
  Newest = "new",
  Featured = "featured",
  LowestPrice = "low-price",
  HighestPrice = "high-price",
}

type SortingOrderToSearchAppQuerySorting = {
  [key in SortingBy]: [SearchAppQuerySortField, SearchAppQuerySortDirection];
};
export const StylesSorting: SortingOrderToSearchAppQuerySorting = {
  [SortingBy.Newest]: ["entity_id", "desc"],
  [SortingBy.Featured]: ["position", "asc"],
  [SortingBy.LowestPrice]: ["price", "asc"],
  [SortingBy.HighestPrice]: ["price", "desc"],
};

export type SearchAppQueryFlags =
  | "check_quantity"
  | "on_demand"
  | "subscription"
  | "subscription_available_now"
  | "no_ex_rental"
  | "ex_rental_only"
  | "sale"
  | "rental"
  | "all_style_images"
  | "unknown_flag";

export interface SearchAppQuery {
  text: string;
  filters: QueryFilters;
  date_range?: string;
  style_codes?: string[];
  postcode?: string;
  field: string;
  flags: SearchAppQueryFlags[];
  paging: {
    limit: number;
    offset: number;
  };
  sorting?: [[SearchAppQuerySortField, SearchAppQuerySortDirection]];
  storefront?: string;
  tiers?: string[];
}

export interface QueryFilters {
  [filterCode: string]: string[][];
}

export interface ResultFilters {
  [key: string]: {
    [key: string]: number;
  };
}

export interface SearchAppResult {
  data: any[];
  count: number;
  filters: ResultFilters;
  impossible_delivery_date: any;
}

export type StyleSize = {
  size: string | "OneSize";
  productCode: string;
};

export type StyleSizeSubscription = StyleSize & { available: boolean };

export interface SearchAppSizesApiResponse {
  [styleCode: string]: {
    sale: StyleSize[];
    rental: StyleSize[];
  };
}

// For cases where returning null or throwing exceptions is too much work
// https://imhoff.blog/posts/using-results-in-typescript
export type SuccessResult<T = undefined> = { ok: true } & (T extends undefined ? Record<any, any> : { value: T });
export type ErrorResult<E> = { ok: false; error: E };
export type Result<T, E = undefined> = SuccessResult<T> | ErrorResult<E>;

export interface CatalogueAppResult extends SearchAppResult {
  html: string;
  page_records_count: number;
  invalidStyles: InvalidStyle[];
}

export interface CatalogueFilterQuery extends SearchAppQuery {
  postcode: string;
  date_range: string;
  storefront: string;
}

export interface DeliveryCalendarQuery {
  postcode?: string;
}

export interface DeliveryCalendarResult {
  available_delivery_dates: string[];
}

export interface ProductCalendarQuery {
  postcode: string;
  productCode: string;
  rentalDays: string;
}

export interface ProductCalendarResult {
  available_delivery_dates: string[]; // YYYY-MM-DD
}

export interface StylingPreferences {
  size?: string[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SearchAppError extends AxiosError {}

export interface InvalidStyle {
  style: Style;
  errors: ErrorObject[];
}

export interface FilterStylesResult {
  validStyles: Style[];
  invalidStyles: InvalidStyle[];
}

export interface AttributeFilter {
  code: string;
  label: string;
  options: AttributeFilterOption[];
}

export interface AttributeFilterItemProps {
  attributeFilter: AttributeFilter;
}

export interface AttributeFilterOption {
  label: string;
  value: string;
}

export interface CatalogueScopeFilters {
  flags: SearchAppQueryFlags[];
  searchText: string;
  styleCodes: string[] | undefined;
  categories: string[][];
  availableDesignations: CollectionDesignation[];
}

export enum CollectionDesignation {
  All = "all",
  AvailableToBuy = "available-to-buy",
  OnDemand = "on-demand",
  Subscription = "subscription",
}

export interface CollectionFilter {
  collectionDesignation: CollectionDesignation;
  sorting: SortingBy;
}

export interface CatalogueReviews {
  count: number;
  averageRating: number;
  reviews: ReviewData[];
}

export interface ReviewData {
  date: string;
  datetime: string;
  customer: string;
  text: string;
  title: string;
  product: string;
  url: string;
  rating: number;
  location?: string;
}

export enum StyleDesignations {
  BUY = "buy",
  RENTAL = "rental",
  SUBSCRIPTION = "subscription",
}

export interface CatalogueSizeAvailabilityBody {
  [styleCode: string]: {
    [productCode: string]: {
      is_available_now: boolean;
    };
  };
}

// Much more focused subset of the default catalogue style
export interface StylePageStyle
  extends Pick<
    Style,
    | "style_code"
    | "designations_include_rental"
    | "designations_include_sale"
    | "designations_include_subscription"
    | "discount_buy_price_in_cents"
    | "retail_price_in_cents"
    | "rental_prices"
    | "buy_price_in_cents"
    | "name"
    | "designer"
    | "url"
    | "image_info"
  > {
  description: string;
  category_ids: number[]; // Magento category ID
}

export interface StylePageSizes {
  sale: StyleSize[];
  rental: StyleSize[];
}
export type StylePageSubscriptionSizes = Record<string, { is_available_now: boolean }> | undefined;

export type RentalPrice = Exclude<Style["rental_prices"], null>[number];
